// 📄 B366 (AI_156) — "ESTE ARQUIVO É UM PDF?" PELO CONTEÚDO, NÃO PELO RÓTULO
// ─────────────────────────────────────────────────────────────
// O import de exames lab rejeitava o arquivo com `file.type !== "application/pdf"`.
// `file.type` é informado pelo NAVEGADOR e vem VAZIO em casos comuns no uso real:
// PDF trazido de aplicativo de digitalização, do WhatsApp, de nuvem, ou escolhido
// por alguns celulares/tablets. O arquivo era um PDF e a tela dizia "Selecione um
// arquivo PDF." — explica falhar na equipe (celular) e funcionar no Mac.
// (Equipe de Rondônia, 29/10/2026.)
//
// A troca não é "aceitar pela extensão": é olhar os BYTES. Todo PDF começa com a
// assinatura "%PDF-". Isso é mais FORTE que o teste antigo (um JPEG renomeado com
// `type` application/pdf passava antes e agora não passa) e não depende do
// aparelho. Importa porque o servidor NÃO confere o conteúdo: ele só limita o
// tamanho e manda o arquivo à IA como `media_type: application/pdf`.
//
// Tolerância: a assinatura deve estar no início, mas há geradores que deixam
// bytes antes dela; leitores tolerantes aceitam dentro dos primeiros 1024 bytes.
// ─────────────────────────────────────────────────────────────

/** MIMEs que declaram PDF (inclui apelidos legados vistos em navegadores). */
const MIME_PDF = new Set([
  "application/pdf",
  "application/x-pdf",
  "application/acrobat",
  "application/vnd.pdf",
  "text/pdf",
  "text/x-pdf",
]);

export function mimeDeclaraPdf(tipo?: string | null): boolean {
  return MIME_PDF.has(String(tipo || "").trim().toLowerCase());
}

export function nomeTerminaEmPdf(nome?: string | null): boolean {
  return /\.pdf$/i.test(String(nome || "").trim());
}

/**
 * `true` se o conteúdo começa com a assinatura de PDF ("%PDF-"), procurada nos
 * primeiros `janela` bytes. Recebe o base64 PURO (sem o prefixo `data:`).
 */
export function conteudoEhPdf(base64: string, janela = 1024): boolean {
  if (typeof base64 !== "string" || base64.length === 0) return false;
  // 4 chars de base64 = 3 bytes; pegar com folga e decodificar só o começo.
  const pedaco = base64.slice(0, Math.ceil((janela / 3) * 4));
  let texto: string;
  try {
    texto = atob(pedaco);
  } catch {
    return false; // base64 inválido
  }
  return texto.slice(0, janela).includes("%PDF-");
}

/** Mensagem de recusa, ou "" quando o arquivo passa. Mensagem é para o usuário. */
export function recusarSePdfInvalido(
  arquivo: { name?: string; type?: string; size?: number },
  base64: string,
  maxBytes = 25 * 1024 * 1024
): string {
  if (typeof arquivo?.size === "number" && arquivo.size > maxBytes) {
    return `Arquivo muito grande (máximo ${Math.round(maxBytes / 1024 / 1024)} MB).`;
  }
  if (conteudoEhPdf(base64)) return "";
  // Conteúdo não confere. Se o rótulo/nome diziam PDF, o arquivo está corrompido
  // ou é outro formato renomeado — vale distinguir para o usuário saber o que fazer.
  if (mimeDeclaraPdf(arquivo?.type) || nomeTerminaEmPdf(arquivo?.name)) {
    return "O arquivo tem extensão .pdf mas o conteúdo não é um PDF válido (pode estar corrompido ou ser uma imagem renomeada).";
  }
  return "Selecione um arquivo PDF.";
}
